<template>
  <div>
    <div v-if="isLoadingSummary" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading summary data...</p>
    </div>

    <div v-if="!isLoadingSummary" class="unenrollment-summary-wrapper mb-4">
      <div class="unenrollment-overview-card">
        <div class="unenrollment-summary-header">
          <div>
            <span class="summary-eyebrow">This Year</span>
            <h5 class="mb-0">IDP Un-Enrollment Summary</h5>
          </div>
          <div class="summary-rate">
            <strong>{{ currentProgressPercentage }}%</strong>
            <span>un-enrollment progress</span>
          </div>
        </div>

        <div class="progress mb-3" style="height: 18px">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="`width: ${currentProgressPercentage}%;`"
            :aria-valuenow="currentProgressPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ currentProgressPercentage }}%
          </div>
        </div>

        <div class="summary-card-grid">
          <div class="status-card bg-teal">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check-circle"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Enrolled</p>
              <h3 class="mb-0">{{ currentSummary.enrolled }}</h3>
            </div>
          </div>

          <div class="status-card bg-danger">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-clock"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Need Approval</p>
              <h3 class="mb-0">{{ currentSummary.need_approval }}</h3>
            </div>
          </div>

          <div class="status-card bg-success">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-log-out"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Unenrolled</p>
              <h3 class="mb-0">{{ currentSummary.unenrolled }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div class="last-year-card">
        <span class="summary-eyebrow">Last Year</span>
        <h5 class="mb-3">IDP Un-Enrollment</h5>
        <div class="last-year-list">
          <div>
            <span>Enrolled</span>
            <strong>{{ previousSummary.enrolled }}</strong>
          </div>
          <div>
            <span>Need Approval</span>
            <strong>{{ previousSummary.need_approval }}</strong>
          </div>
          <div>
            <span>Unenrolled</span>
            <strong>{{ previousSummary.unenrolled }}</strong>
          </div>
        </div>
        <div class="last-year-progress">
          <div :style="`width: ${previousProgressPercentage}%;`"></div>
        </div>
        <small>{{ previousProgressPercentage }}% un-enrollment progress</small>
      </div>

      <div class="unenrollment-roadmap-card">
        <div class="unenrollment-summary-header">
          <div>
            <span class="summary-eyebrow">Roadmap</span>
            <h5 class="mb-0">Proses Un-Enrollment IDP</h5>
          </div>
        </div>

        <div class="unenrollment-roadmap">
          <div v-for="step in roadmapSteps" :key="step.label" class="roadmap-step">
            <div class="roadmap-marker">
              <i :class="step.icon"></i>
            </div>
            <span>{{ step.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Summary",
  props: {
    isLoadingSummary: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Object,
      default: () => ({
        previous: { enrolled: 0, need_approval: 0, unenrolled: 0, percentage: 0 },
        current: { enrolled: 0, need_approval: 0, unenrolled: 0, percentage: 0 },
      }),
    },
  },
  computed: {
    currentSummary() {
      return this.normalizeSummary(this.summaryData?.current);
    },
    previousSummary() {
      return this.normalizeSummary(this.summaryData?.previous);
    },
    currentProgressPercentage() {
      return this.calculateProgress(this.currentSummary);
    },
    previousProgressPercentage() {
      return this.calculateProgress(this.previousSummary);
    },
    roadmapSteps() {
      return [
        { label: "Enrolled", icon: "fe fe-check-circle" },
        { label: "Need Approval", icon: "fe fe-clock" },
        { label: "Unenrolled", icon: "fe fe-log-out" },
      ];
    },
  },
  methods: {
    normalizeSummary(summary = {}) {
      return {
        enrolled: Number(summary?.enrolled ?? summary?.approved ?? 0),
        need_approval: Number(summary?.need_approval || 0),
        unenrolled: Number(summary?.unenrolled ?? summary?.open ?? summary?.plan ?? 0),
        total: Number(summary?.total || 0),
        percentage: Number(summary?.percentage || 0),
      };
    },
    calculateProgress(summary) {
      const total = summary.total || summary.enrolled + summary.need_approval + summary.unenrolled;
      if (!total) return 0;

      return Math.min(Math.round(((summary.need_approval + summary.unenrolled) / total) * 100), 100);
    },
  },
};
</script>

<style>
.unenrollment-summary-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
}

.unenrollment-overview-card,
.last-year-card,
.unenrollment-roadmap-card {
  border: 1px solid #e6edf7;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.04);
}

.unenrollment-roadmap-card {
  grid-column: 1 / -1;
}

.unenrollment-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-eyebrow {
  display: block;
  color: #90a4c4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #5e6b7f;
}

.summary-rate strong {
  color: #b80000;
  font-size: 22px;
  line-height: 1;
}

.summary-rate span,
.last-year-card small {
  font-size: 12px;
  font-weight: 700;
}

.summary-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.status-card {
  border-radius: 8px;
  padding: 16px;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.status-card p {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0;
}

.status-card h3 {
  font-size: 32px;
  font-weight: bold;
  margin: 0;
}

.bg-teal {
  background-color: #5bc0be;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  font-weight: 700;
  font-size: 12px;
}

.last-year-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.last-year-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf1f6;
  padding-bottom: 8px;
}

.last-year-list span {
  color: #5e6b7f;
  font-size: 12px;
  font-weight: 700;
}

.last-year-list strong {
  color: #14213d;
  font-size: 22px;
  line-height: 1;
}

.last-year-progress {
  height: 8px;
  border-radius: 8px;
  background: #edf1f6;
  overflow: hidden;
  margin-bottom: 8px;
}

.last-year-progress div {
  height: 100%;
  border-radius: inherit;
  background: #b80000;
}

.unenrollment-roadmap {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding-top: 8px;
}

.unenrollment-roadmap::before {
  content: "";
  position: absolute;
  top: 22px;
  left: 17px;
  right: 17px;
  height: 2px;
  background: #dbe4f0;
}

.roadmap-step {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.roadmap-marker {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #08ce79;
  background: #eafff5;
  color: #08a867;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  font-size: 13px;
}

.roadmap-step span {
  color: #14213d;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 991.98px) {
  .unenrollment-summary-wrapper,
  .summary-card-grid,
  .unenrollment-roadmap {
    grid-template-columns: 1fr;
  }

  .unenrollment-roadmap::before {
    display: none;
  }
}

@media (max-width: 575.98px) {
  .unenrollment-summary-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-rate {
    align-items: flex-start;
  }
}
</style>
